/**
 * Shared icon catalog for metric tiles.
 *
 * `chart_config.metric.icon` may be:
 *   - undefined / 'auto'  → derive from `format`
 *   - 'none'              → render nothing
 *   - any key from ICON_PRESETS below
 *
 * Keys are stable strings (persisted in the DB), so do not rename them.
 */
import React from 'react';
import {
  DollarOutlined, PercentageOutlined, NumberOutlined,
  UserOutlined, TeamOutlined, ClockCircleOutlined,
  FireOutlined, ThunderboltOutlined, RiseOutlined, FallOutlined,
  CheckCircleOutlined, ShoppingCartOutlined,
  LineChartOutlined, BarChartOutlined, StarOutlined, TrophyOutlined,
} from '@ant-design/icons';

export const ICON_PRESETS = {
  dollar:     { label: 'Dollar',     Component: DollarOutlined },
  percent:    { label: 'Percent',    Component: PercentageOutlined },
  number:     { label: 'Number',     Component: NumberOutlined },
  user:       { label: 'User',       Component: UserOutlined },
  team:       { label: 'Team',       Component: TeamOutlined },
  clock:      { label: 'Time',       Component: ClockCircleOutlined },
  fire:       { label: 'Fire',       Component: FireOutlined },
  thunder:    { label: 'Bolt',       Component: ThunderboltOutlined },
  rise:       { label: 'Up',         Component: RiseOutlined },
  fall:       { label: 'Down',       Component: FallOutlined },
  check:      { label: 'Check',      Component: CheckCircleOutlined },
  cart:       { label: 'Cart',       Component: ShoppingCartOutlined },
  line:       { label: 'Line',       Component: LineChartOutlined },
  bar:        { label: 'Bar',        Component: BarChartOutlined },
  star:       { label: 'Star',       Component: StarOutlined },
  trophy:     { label: 'Trophy',     Component: TrophyOutlined },
};

/** Auto-pick an icon key from the format when icon is unset / 'auto'. */
export function autoIconForFormat(format) {
  switch ((format || '').toLowerCase()) {
    case 'usd':     return 'dollar';
    case 'percent': return 'percent';
    default:        return null; // number / compact → no icon by default
  }
}

/** Resolve a config to a concrete icon key (or null for none). */
export function resolveIconKey(cfg) {
  const raw = cfg?.icon;
  if (raw === 'none') return null;
  if (!raw || raw === 'auto') return autoIconForFormat(cfg?.format);
  return ICON_PRESETS[raw] ? raw : autoIconForFormat(cfg?.format);
}

/** Render the icon for a metric config, or null. */
export function MetricIcon({ cfg, style }) {
  const key = resolveIconKey(cfg);
  if (!key) return null;
  const Cmp = ICON_PRESETS[key].Component;
  return <Cmp style={style} />;
}

/** Curated color swatches for the editor. `null` ⇒ theme default. */
export const COLOR_SWATCHES = [
  { value: null,        label: 'Default' },
  { value: '#1677ff',   label: 'Blue' },
  { value: '#52c41a',   label: 'Green' },
  { value: '#fa8c16',   label: 'Orange' },
  { value: '#f5222d',   label: 'Red' },
  { value: '#722ed1',   label: 'Purple' },
  { value: '#13c2c2',   label: 'Teal' },
  { value: '#eb2f96',   label: 'Pink' },
  { value: '#faad14',   label: 'Gold' },
  { value: '#595959',   label: 'Grey' },
];
